import { ApiResourceBaseClass as o } from "./baseApiCalls.js";
function n(r) {
  return r.map((t) => new Date(t)).sort((t, e) => t.getTime() - e.getTime());
}
class c extends o {
  uri = "competitive-benchmark/settings";
  /**
   * Retrieves all the keywords for the current organization
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/competitive-benchmark/settings/keywords`
   *
   * @returns {Promise<SettingsKeyword[]>} A promise that resolves the keywords with their associated data.
   */
  getKeywords = async () => {
    const { data: t } = await this.get(`${this.uri}/keywords`);
    return t.keywords;
  };
  /**
   * Deletes a given keyword
   *
   * HTTP Verb: `DELETE`
   * Endpoint URL: `/competitive-benchmark/settings/keywords/{keyword_id}`
   *
   * @param {string} id - The id of the keyword
   * @returns {Promise<void>}
   */
  deleteKeyword = async (t) => {
    await this.delete(`${this.uri}/keywords/${t}`);
  };
  /**
   * Toggles a given keyword on or off
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/competitive-benchmark/settings/keywords/{keyword_id}/toggle`
   *
   * @param {string} id - The id of the keyword
   * @param {boolean} active - The new active state
   * @returns {Promise<Keyword>}
   */
  toggleKeyword = async (t, e) => {
    const { data: s } = await this.post(`${this.uri}/keywords/${t}/toggle`, { active: e });
    return s;
  };
  /**
   * Updates a given keyword including their associated groups and competitors
   *
   * HTTP Verb: `PUT`
   * Endpoint URL: `/competitive-benchmark/settings/keywords/{keyword_id}`
   * @param {string} id - The id of the keyword
   * @param {number[]} groups - The new list of groups
   * @param {Competitor[]} competitors - The new list of competitors
   * @returns {Promise<Keyword>}
   */
  updateKeyword = async (t, e, s) => {
    const { data: a } = await this.put(`${this.uri}/keywords/${t}`, {
      groups: e,
      competitors: s
    });
    return a;
  };
  /**
   * Creates a new empty keyword
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/competitive-benchmark/settings/keywords`
   *
   * @param {string} name - The name of the keyword
   * @returns {Promise<Keyword>}
   */
  createKeyword = async (t) => {
    const { data: e } = await this.post(`${this.uri}/keywords`, {
      name: t,
      groups: [],
      competitors: []
    });
    return e;
  };
}
class u extends o {
  uri = "competitive-benchmark";
  /**
   * Retrieves all the available keywords for the current user
   *
   * Includes the keywords associated groups and sorted dates
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/competitive-benchmark/keywords`
   *
   * @returns {Promise<Keyword[]>}
   */
  getKeywords = async () => {
    const { data: t } = await this.get(
      `${this.uri}/keywords`
    );
    return t ? t.available_keywords.map((e) => ({
      keyword: e.keyword,
      groups: e.groups.map((s) => ({ id: [s.id.toString()], label: s.name })),
      dates: n(e.dates)
    })) : [];
  };
  /**
       * Retrieves the overview data
       *
       * HTTP Verb: `GET`
       * Endpoint URL: `/competitive-benchmark/overview`
       *
       * @param {string} keyword - The name of the keyword
       * @param {string} start - The start date (as a 2019-08-24)
       * @param {string} end - The end date (as a 2019-08-24)
       * @param {BusinessModalFiltersType} filters - The business modal filters
  
       * @returns {Promise<Overview>}
       */
  getOverview = async (t, e, s, a) => {
    const { data: i } = await this.get(`${this.uri}/overview`, {
      keyword: t,
      start_date: e,
      end_date: s,
      ...a
    });
    return i ? {
      dates: n(i.overview.dates),
      businesses: i.overview.businesses
    } : { dates: [], businesses: [] };
  };
  /**
   * Retrieves the central ranks for a given keyword and date
   *
   * Results are filtered by the business modal filters
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/competitive-benchmark/central`
   *
   * @param {string} keyword - The name of the keyword
   * @param {string} period - The period of the data to send, day is ignored (as a 2019-08-24)
   * @param {BusinessModalFiltersType} filters - The business modal filters
   * @returns {Promise<CentralRank[]>}
   */
  getCentralRanks = async (t, e, s) => {
    const { data: a } = await this.get(`${this.uri}/central`, {
      keyword: t,
      date: e,
      ...s
    });
    return a?.ranks ?? [];
  };
  /**
   * Retrieves a business local ranks for a given keyword and date
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/competitive-benchmark/local`
   *
   * @param {string} keyword - The name of the keyword
   * @param {string} period - The period of the data to send, day is ignored (as a 2019-08-24)
   * @param {string} business - The business id
   * @returns {Promise<LocalRank[]>}
   */
  getLocalRanks = async (t, e, s) => {
    const { data: a } = await this.get(`${this.uri}/local`, {
      keyword: t,
      date: e,
      business: s
    });
    return a?.ranks ?? [];
  };
  /**
   * Starts an asynchronous data export sent to the given email address
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/competitive-benchmark/export`
   *
   *
   * @param {string} email - The email to send the generated data export to
   * @param {ExportSelectionType} type - The type of export to generate
   * @param {string} period - The period of the data to send, day is ignored (as a 2019-08-24)
   * @returns {Promise<void>}
   */
  postExportTask = async (t, e, s) => {
    await this.post(`${this.uri}/export`, { email: t, type: e, month_date: s });
  };
}
export {
  u as CompetitiveBenchmark,
  c as CompetitiveBenchmarkSettings
};
